/**
 * Copy for the Billing welcome tour step.
 *
 * The step lists the tabs the user can actually see. The tabs are permission
 * filtered and other work may rename them ("Transactions" → "Payments"), so the
 * labels are read from the rendered tab menu when the step opens, and each one
 * is matched to its one-line description by a normalised key.
 */

/** Selector for the Billing tab menu (BillingLayout.vue). */
export const BILLING_TAB_MENU_SELECTOR = '#billing-tab-menu';

/** The Billing menu phones get instead of the tab bar (BillingNavSelect.vue). */
export const BILLING_NAV_SELECT_SELECTOR = '[data-testid="billing-nav-select"]';

/** Used when the tab menu can't be read (not rendered yet, or a test). */
export const DEFAULT_BILLING_TAB_LABELS = [
  'Dashboard',
  'Students',
  'Bills',
  'Transactions',
  'Form Fees',
  'Debtors',
  'Items',
  'Discounts',
  'Refunds',
  'Reports',
];

const PAYMENTS = 'every payment received. Record cash, bank transfer and POS payments here';
const FEE_ITEMS = 'your saved fees (for example Tuition or Lab fee), ready to add to bills';
const REFUNDS = 'refunds on payments, and exemptions that let a student off part or all of a bill';

const TAB_DESCRIPTIONS: Record<string, string> = {
  dashboard: 'money billed, collected and still owed, at a glance',
  students: 'each student\'s account: what they were billed, what they paid and their balance owed',
  bills: 'create bills for one student, a whole class or a class section, then publish them so parents can pay',
  transactions: PAYMENTS,
  payments: PAYMENTS,
  formfees: 'application and form fees paid by applicants',
  waivercodes: 'waiver codes for form fees, by campus: which are available, and who used the rest and when',
  debtors: 'students who still owe money, with a list you can download',
  items: FEE_ITEMS,
  feeitems: FEE_ITEMS,
  discounts: 'discounts taken off automatically, such as staff child or scholarship',
  refunds: REFUNDS,
  refundsexemptions: REFUNDS,
  reports: 'summaries for a term or date range, on screen or as a download',
};

/** 'Refunds & Exemptions' → 'refundsexemptions'; 'Form Fees' → 'formfees'. */
export function normaliseTabLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/&|\band\b/g, '')
    .replace(/[^a-z]/g, '');
}

/** The one-line description for a tab label, or null for a tab we don't know. */
export function billingTabDescription(label: string): string | null {
  return TAB_DESCRIPTIONS[normaliseTabLabel(label)] ?? null;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** A group of Billing pages as the tab bar publishes it for the tour. */
export interface BillingTabGroupForTour {
  label: string;
  pages: string[];
}

/**
 * Groups as `data-tab-groups`: "Overview:Dashboard,Reports|Payments:Payments,…".
 * Page and group names never contain ":", "," or "|".
 */
export function serializeBillingGroups(groups: Array<{ label: string; pages: Array<{ label: string }> }>): string {
  return groups.map((group) => `${group.label}:${group.pages.map((page) => page.label).join(',')}`).join('|');
}

/** The groups the tab bar published, or null when it publishes none. */
export function readBillingTabGroups(root: ParentNode = document): BillingTabGroupForTour[] | null {
  const published = root.querySelector<HTMLElement>(BILLING_TAB_MENU_SELECTOR)?.dataset.tabGroups;
  if (!published) return null;
  const groups = published.split('|')
    .map((entry) => {
      const [label = '', pages = ''] = entry.split(':');
      return { label: label.trim(), pages: pages.split(',').map((page) => page.trim()).filter(Boolean) };
    })
    .filter((group) => group.label && group.pages.length);
  return groups.length ? groups : null;
}

function pageLine(label: string): string {
  const description = billingTabDescription(label);
  const name = `<strong>${escapeHtml(label)}</strong>`;
  return `<li>${description ? `${name}: ${description}` : name}</li>`;
}

/**
 * The welcome step body. With groups, it explains the grouped tabs and lists
 * each group's pages; otherwise one line per visible tab. The tour library
 * renders a string step body as HTML, so labels are escaped.
 */
export function billingWelcomeContent(
  labels: string[],
  groups: BillingTabGroupForTour[] | null = null,
  onPhone = false,
): string {
  if (groups?.length) {
    const items = groups
      .map((group) => `<li><strong>${escapeHtml(group.label)}</strong><ul>${group.pages.map(pageLine).join('')}</ul></li>`)
      .join('');
    const how = onPhone
      ? 'Billing\'s pages are grouped by job. Open this menu and pick a page:'
      : 'Billing is grouped into tabs. Click a tab, then choose a page in the row underneath it:';
    return `${how}<ul>${items}</ul>`;
  }

  const tabs = labels.length ? labels : DEFAULT_BILLING_TAB_LABELS;
  const items = tabs.map(pageLine).join('');
  return `Billing is split into tabs. Click a tab to open it:<ul>${items}</ul>`;
}

/**
 * The Debtors row "Record payment" button (DebtorsView.vue). It only renders
 * for staff allowed to record payments, and only when the list has rows.
 */
export const DEBTOR_RECORD_PAYMENT_SELECTOR = '[data-testid^="btn-debtor-record-payment-"]';

/**
 * The Debtors table step body. It mentions the row "Record payment" button
 * only when the page actually shows one, so the tour never promises a button
 * the user can't see.
 */
export function debtorsTableContent(root: ParentNode = document): string {
  const intro = 'Each row shows what the student was billed, what they have paid and their balance owed. '
    + 'The total owed by everyone in the list is shown under the table.';
  const action = root.querySelector(DEBTOR_RECORD_PAYMENT_SELECTOR)
    ? 'Use Record payment on a student\'s row when a parent pays, or click the student to open their Student account.'
    : 'Click a student to open their Student account, where you can record a payment.';
  return `${intro} ${action}`;
}

/**
 * Labels of the Billing pages the user can open, in order. The tab bar
 * publishes them on `data-tab-labels` (pipe-separated): its tabs are groups,
 * so most pages are not tabs at all. Empty when the bar isn't on the page.
 */
export function readBillingTabLabels(root: ParentNode = document): string[] {
  const published = root.querySelector<HTMLElement>(BILLING_TAB_MENU_SELECTOR)?.dataset.tabLabels ?? '';
  return published.split('|').map((label) => label.trim()).filter(Boolean);
}
